/**
 * Add To Cart Integration Component
 * 
 * Handles cart operations and checkout actions
 */

import { useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

const AddToCartIntegration = ({
    productId,
    variationId,
    quantity,
    stockPicks,
    isGuest,
    localization,
    onAddToCart,
    disabled,
    isPreorder = false
}) => {
    const [loading, setLoading] = useState(false);
    const [cartStatus, setCartStatus] = useState('');
    
    /**
     * Handle add to cart action
     */
    const handleAddToCart = async () => {
        if (disabled || loading) return;
        
        setLoading(true);
        setCartStatus('');
        
        try {
            // Prepare cart data
            const cartData = {
                product_id: productId,
                variation_id: variationId || 0,
                quantity: quantity,
                stock_picks: stockPicks || [],
                is_preorder: isPreorder
            };
            
            // Call the API to add to cart
            const response = await fetch(
                `${localization.apiUrl}add-to-cart`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': localization.nonce
                    },
                    body: JSON.stringify(cartData)
                }
            );
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                setCartStatus('success');
                
                // Execute cart fragments JavaScript to update Flatsome mini-cart
                if (result.fragments_js) {
                    try {
                        // Create and execute script to trigger WooCommerce cart fragments update
                        const script = document.createElement('script');
                        script.textContent = result.fragments_js;
                        document.head.appendChild(script);
                        document.head.removeChild(script);
                    } catch (fragmentsError) {
                        // Silent fail for fragments JS execution
                    }
                }
                
                // Trigger parent callback
                if (onAddToCart) {
                    onAddToCart(result);
                }
                
                // Show success message briefly
                setTimeout(() => setCartStatus(''), 3000);
                
            } else {
                throw new Error(result.message || __('加入購物車失敗', 'frontend-order-system'));
            }
            
        } catch (error) {
            setCartStatus('error');
            
            // Show error message briefly
            setTimeout(() => setCartStatus(''), 5000);
            
        } finally {
            setLoading(false);
        }
    };
    
    
    /**
     * Get button text based on product type
     */
    const getAddToCartText = () => {
        if (loading) return __('處理中...', 'frontend-order-system');
        if (isPreorder) return __('加入預訂車', 'frontend-order-system');
        return __('加入購物車', 'frontend-order-system');
    };
    
    
    return (
        <div className="fos-cart-integration">
            {/* Action Buttons */}
            <div className="fos-cart-actions" style={{
                display: 'flex',
                gap: '12px',
                padding: '20px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e1e1e1',
                marginTop: '20px'
            }}>
                {/* Add to Cart Button */}
                <button
                    type="button"
                    className="fos-btn fos-btn-secondary"
                    onClick={handleAddToCart}
                    disabled={disabled || loading}
                    style={{
                        flex: 1,
                        minHeight: '48px',
                        fontSize: '1rem',
                        fontWeight: '500',
                        opacity: disabled ? 0.6 : 1,
                        cursor: disabled ? 'not-allowed' : 'pointer'
                    }}
                    aria-label={getAddToCartText()}
                >
                    {cartStatus === 'success' ? (
                        <span style={{ color: '#28a745' }}>
                            ✓ {__('已加入', 'frontend-order-system')}
                        </span>
                    ) : (
                        <>
                            🛒 {getAddToCartText()}
                        </>
                    )}
                </button>
                
            </div>
            
            {/* Status Messages */}
            {cartStatus === 'error' && (
                <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: '#f8d7da',
                    color: '#721c24',
                    border: '1px solid #f5c6cb',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    textAlign: 'center'
                }}>
                    <span>⚠️ {__('操作失敗，請重試', 'frontend-order-system')}</span>
                </div>
            )}
            
            {cartStatus === 'success' && (
                <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: '#d4edda',
                    color: '#155724',
                    border: '1px solid #c3e6cb',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    textAlign: 'center'
                }}>
                    <span>✓ {__('已成功加入購物車', 'frontend-order-system')}</span>
                </div>
            )}
            
            {/* Guest User Notice */}
            {isGuest && (
                <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: '#fff3cd',
                    color: '#856404',
                    border: '1px solid #ffeaa7',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    textAlign: 'center'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <span>👤</span>
                        <span>
                            {__('您正以訪客身份購物', 'frontend-order-system')}
                            {!isPreorder && (
                                <>
                                    {' • '}
                                    <a 
                                        href="/wp-login.php" 
                                        style={{ 
                                            color: '#856404', 
                                            textDecoration: 'underline',
                                            fontWeight: '500'
                                        }}
                                    >
                                        {__('登入獲得更好體驗', 'frontend-order-system')}
                                    </a>
                                </>
                            )}
                        </span>
                    </div>
                </div>
            )}
            
            {/* Disabled State Message */}
            {disabled && (
                <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: '#f8f9fa',
                    color: '#666',
                    border: '1px solid #e1e1e1',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    textAlign: 'center'
                }}>
                    <span>
                        {quantity === 0 
                            ? __('請設定數量後再進行操作', 'frontend-order-system')
                            : __('請完成選擇後再進行操作', 'frontend-order-system')
                        }
                    </span>
                </div>
            )}
            
            {/* Product Type Indicator */}
            <div style={{
                marginTop: '8px',
                padding: '8px 12px',
                background: isPreorder ? '#e7f3ff' : '#f0f8f0',
                color: isPreorder ? '#0073aa' : '#155724',
                border: `1px solid ${isPreorder ? '#bee5eb' : '#c3e6cb'}`,
                borderRadius: '4px',
                fontSize: '0.8rem',
                textAlign: 'center'
            }}>
                <span>
                    {isPreorder ? (
                        <>📋 {__('預訂商品', 'frontend-order-system')}</>
                    ) : (
                        <>📦 {__('現貨商品', 'frontend-order-system')}</>
                    )}
                </span>
            </div>
        </div>
    );
};

export default AddToCartIntegration;